import React, { useState, useEffect } from 'react';
import { Chess } from 'chess.js';
import ChessBoard from '../../components/ChessBoard';
import api from '../../api/client';
import { engineManager, EngineEvaluation } from '../game/engine/engineManager';
import { RefreshCw, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Brain, RotateCcw, Heart, BookOpen, Sparkles, Terminal, BarChart2 } from 'lucide-react';
import { getOpeningData, buildEvidencePackage, EvidencePackage } from '@chess-platform/shared';
import CoachPanel from '../../components/CoachPanel';

interface OpeningsTheoryProps {
    eco: string;
    color?: 'w' | 'b';
    onExit?: () => void;
}

const OpeningsTheory: React.FC<OpeningsTheoryProps> = ({ eco: selectedEco, color: initialColor, onExit }) => {
    const [openingDetail, setOpeningDetail] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [currentFen, setCurrentFen] = useState('rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1');
    const [moveHistory, setMoveHistory] = useState<string[]>([]);
    const [mainLine, setMainLine] = useState<string[]>([]);
    const [explanations, setExplanations] = useState<string[]>([]);
    const [inRepertoire, setInRepertoire] = useState(false);
    const [showMobileAnalysis, setShowMobileAnalysis] = useState(false);

    const [engineEval, setEngineEval] = useState<EngineEvaluation | null>(null);
    const [isDeviated, setIsDeviated] = useState(false);

    // Coach/Evidence for free play
    const [evidence, setEvidence] = useState<EvidencePackage | null>(null);
    const [coachExplanation, setCoachExplanation] = useState<{
        short: string;
        details: string[];
        ifInstead?: string;
        warnings?: string[];
    } | null>(null);
    const [coachLoading, setCoachLoading] = useState(false);

    useEffect(() => {
        const unsub = engineManager.subscribe((ev) => setEngineEval(ev));
        return () => unsub();
    }, []);

    useEffect(() => {
        if (selectedEco) {
            loadOpening(selectedEco);
        }
    }, [selectedEco]);

    const loadOpening = async (eco: string) => {
        setLoading(true);
        try {
            let apiData: any = null;
            try {
                const opRes = await api.get(`/openings/${eco}`);
                setOpeningDetail(opRes.data);
                apiData = opRes.data.dataJson ? JSON.parse(opRes.data.dataJson) : null;
            } catch (err) { }

            const repRes = await api.get('/repertoire').catch(() => ({ data: [] }));
            setInRepertoire(repRes.data.some((r: any) => r.eco === eco));

            const bookData = getOpeningData(eco);

            if (bookData) {
                if (!openingDetail) setOpeningDetail({ eco, name: bookData.name });
                setMainLine(bookData.moves || []);
                setExplanations(bookData.explanations || []);
            } else if (apiData) {
                setMainLine(apiData.mainLine || []);
                setExplanations(apiData.explanations || []);
            } else {
                setMainLine([]);
                setExplanations([]);
            }

            const g = new Chess();
            setCurrentFen(g.fen());
            setMoveHistory([]);
            setIsDeviated(false);
            setEvidence(null);
            setCoachExplanation(null);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleMove = async (move: { from: string; to: string; promotion?: string }) => {
        const previousFen = currentFen;
        const g = new Chess(currentFen);
        const result = g.move(move);
        if (!result) return;

        const moveStr = `${move.from}${move.to}${move.promotion || ''}`;
        const newHistory = [...moveHistory, result.san];

        setCurrentFen(g.fen());
        setMoveHistory(newHistory);

        const expectedMove = mainLine[moveHistory.length];

        let expectedUci = expectedMove;
        if (expectedMove) {
            try {
                if (expectedMove.length < 4 || expectedMove.includes('+') || expectedMove.includes('#') || expectedMove.includes('x') || expectedMove === 'O-O' || expectedMove === 'O-O-O') {
                    const tempGame = new Chess(previousFen);
                    const tempRes = tempGame.move(expectedMove);
                    if (tempRes) {
                        expectedUci = `${tempRes.from}${tempRes.to}${tempRes.promotion || ''}`;
                    }
                }
            } catch (e) { }
        }

        const isMatch = expectedUci && (moveStr === expectedUci);

        if (!isDeviated && !isMatch && moveHistory.length < mainLine.length) {
            setIsDeviated(true);
            setCoachLoading(true);
            setCoachExplanation({ short: 'Calculando análisis fuera de la teoría...', details: [] });
            engineManager.analyze(g.fen(), 'depth 16');
            engineManager.getBestMoveOnce(g.fen(), 14).then(() => {
                setCoachLoading(false);
                setCoachExplanation({ short: 'Entrando en juego libre. El motor seguirá analizando...', details: [] });
            });
        } else if (isDeviated || moveHistory.length >= mainLine.length) {
            engineManager.analyze(g.fen(), 'depth 16');
            if (engineEval && engineEval.score !== null) {
                const prevFen = previousFen;
                setCoachLoading(true);
                const evPkg: EvidencePackage = {
                    ply: moveHistory.length + 1,
                    fenBefore: prevFen,
                    fenAfter: g.fen(),
                    sideThatMoved: result.color,
                    moveSan: result.san,
                    moveUci: `${move.from}${move.to}`,
                    engine: {
                        depth: 16,
                        evalBefore: { type: 'cp', value: Math.round(engineEval.score * 100) },
                        evalAfter: { type: 'cp', value: Math.round(engineEval.score * 100) },
                        deltaCpForMover: 0,
                        bestMoveUci: engineEval.bestMove || '',
                        lines: []
                    },
                    quality: { label: 'good', lossCp: 0 },
                    motifs: [],
                    ui: {
                        suggestedArrows: engineEval.bestMove ? [{ from: engineEval.bestMove.substring(0, 2), to: engineEval.bestMove.substring(2, 4), color: 'rgba(34,197,94,0.6)' }] : [],
                        suggestedHighlights: [],
                        playedMove: { from: move.from, to: move.to },
                        bestMove: engineEval.bestMove ? { from: engineEval.bestMove.substring(0, 2), to: engineEval.bestMove.substring(2, 4) } : undefined
                    }
                };
                setEvidence(evPkg);
                setCoachExplanation({ short: 'Línea completada o desviada. Iniciando análisis bajo demanda.', details: [] });
                setCoachLoading(false);
            }
        } else {
            engineManager.getBestMoveOnce(g.fen(), 12);
            setEvidence(null);
            setCoachExplanation(null);
        }
    };

    const toggleRepertoire = async () => {
        try {
            if (inRepertoire) {
                await api.delete(`/repertoire/${selectedEco}`);
                setInRepertoire(false);
            } else {
                await api.post('/repertoire', { eco: selectedEco, side: initialColor === 'w' ? 'white' : 'black' });
                setInRepertoire(true);
            }
        } catch (err) { }
    };

    const handleBestMove = async () => {
        const best = await engineManager.getBestMoveOnce(currentFen, 18);
        if (best) {
            handleMove({ from: best.substring(0, 2), to: best.substring(2, 4), promotion: best.length === 5 ? best.charAt(4) : undefined });
        }
    };

    const goToIndex = (idx: number) => {
        const g = new Chess();
        const hist: string[] = [];
        for (let i = 0; i < idx; i++) {
            const m = mainLine[i];
            if (!m) break;
            let res;
            if (m.length >= 4 && m.charAt(0) >= 'a' && m.charAt(1) >= '1') {
                res = g.move({ from: m.substring(0, 2), to: m.substring(2, 4), promotion: m.length === 5 ? m.charAt(4) : undefined });
            } else {
                res = g.move(m);
            }
            if (res) hist.push(res.san);
        }
        setCurrentFen(g.fen());
        setMoveHistory(hist);
        if (idx < mainLine.length) {
            setIsDeviated(false);
            setEvidence(null);
            setCoachExplanation(null);
        }
    };

    const handleAskCoach = async () => {
        setCoachLoading(true);
        try {
            const res = await api.post('/ai/coach', { evidence });
            setCoachExplanation(res.data);
        } catch (err) {
            setCoachExplanation({
                short: 'Error al contactar con el Maestro.',
                details: ['Verifica tu conexión o los créditos de IA.'],
                warnings: ['Servicio temporalmente no disponible']
            });
        }
        setCoachLoading(false);
    };

    if (loading || !selectedEco) return <div className="h-full flex flex-col items-center justify-center p-8"><RefreshCw className="animate-spin text-indigo-400 mb-4" /><span className="text-[10px] font-black text-white/20 uppercase">Analizando Línea</span></div>;

    // For when NOT in deviated mode (theory description)
    const currentExplanation = isDeviated ? (coachExplanation?.short || '') : (explanations[Math.floor((moveHistory.length - 1) / 2)] || 'Sigue en la teoría.');

    const customSquareStyles = evidence?.ui.suggestedHighlights.reduce((acc: any, sq: string) => {
        acc[sq] = { backgroundColor: 'rgba(99, 102, 241, 0.2)' };
        return acc;
    }, {}) || {};

    const customArrows = evidence?.ui.suggestedArrows.map(a => [a.from, a.to, a.color]) || (engineEval?.bestMove ? [
        [engineEval.bestMove.substring(0, 2), engineEval.bestMove.substring(2, 4), 'rgba(34,197,94,0.6)']
    ] : []);

    return (
        <div className="flex flex-col lg:flex-row h-full overflow-hidden p-2 lg:p-8 animate-in fade-in bg-[#0a0c10] text-white selection:bg-indigo-500/30">
            {/* AREA DEL TABLERO */}
            <div className="flex-[3] flex flex-col gap-2 lg:gap-4 min-h-0 relative">
                {/* Info & Status Header */}
                <div className="flex items-center justify-between px-2 py-1 lg:py-0">
                    <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-[8px] lg:text-[10px] font-black uppercase tracking-widest text-indigo-400">
                            <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 shadow-[0_0_8px_rgba(129,140,248,0.5)] animate-pulse" />
                            <span>{isDeviated ? 'Juego Libre' : 'Libro de Teoría'}</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={toggleRepertoire} className={`p-2 rounded-xl transition-all border ${inRepertoire ? 'bg-rose-500/10 border-rose-500/20 text-rose-500 shadow-lg' : 'bg-white/5 border-white/5 text-white/20'}`}>
                            <Heart size={14} fill={inRepertoire ? "currentColor" : "none"} />
                        </button>
                    </div>
                </div>

                {/* Board container */}
                <div className="flex-1 flex flex-col relative min-h-0">
                    <div className="flex-1 flex items-center justify-center p-2 lg:p-4 overflow-hidden relative">
                        <div className="w-full h-full max-w-[85vh] aspect-square relative">
                            {/* Evaluation Bar Overlay for Theory */}
                            {engineEval && (
                                <div className="absolute top-2 right-2 lg:top-4 lg:right-4 z-20 flex flex-col items-end gap-1">
                                    <div className="bg-black/80 backdrop-blur-md px-3 py-1 lg:py-1.5 rounded-xl border border-white/10 text-xs lg:text-sm font-black tabular-nums font-mono text-indigo-400">
                                        {(engineEval.score ?? 0) >= 0 ? '+' : ''}{(engineEval.score ?? 0).toFixed(1)}
                                    </div>
                                    <button onClick={handleBestMove} className="bg-green-500/10 hover:bg-green-500/20 px-2 py-1 rounded-lg border border-green-500/20 text-[8px] font-black text-green-400 uppercase tracking-widest transition-all">Pista</button>
                                </div>
                            )}

                            <ChessBoard
                                fen={currentFen}
                                onMove={handleMove}
                                orientation={initialColor === 'b' ? 'black' : 'white'}
                                draggable={true}
                                customArrows={customArrows as any}
                                customSquareStyles={customSquareStyles}
                            />
                        </div>
                    </div>
                </div>

                {/* Navigation Controls */}
                <div className="flex items-center justify-center gap-3 lg:gap-4 py-2 shrink-0 px-4">
                    <button onClick={() => goToIndex(0)} className="p-3 rounded-2xl bg-white/5 border border-white/5 text-white/20 hover:bg-white/10 hover:text-white transition-all"><RotateCcw size={18} /></button>
                    <div className="flex items-center bg-white/5 rounded-2xl border border-white/5 p-1 gap-1">
                        <button onClick={() => goToIndex(Math.max(0, moveHistory.length - 1))} className="p-3 rounded-xl hover:bg-white/5 disabled:opacity-10 text-white/60"><ChevronLeft size={20} /></button>
                        <div className="px-4 text-[10px] font-black tabular-nums text-white/30 tracking-[0.2em]">{moveHistory.length} / {mainLine.length}</div>
                        <button onClick={() => goToIndex(Math.min(mainLine.length, moveHistory.length + 1))} className="p-3 rounded-xl hover:bg-white/5 disabled:opacity-10 text-white/60"><ChevronRight size={20} /></button>
                    </div>

                    {/* MOBILE FEED TOGGLE */}
                    <button
                        onClick={() => setShowMobileAnalysis(!showMobileAnalysis)}
                        className={`lg:hidden p-3 rounded-2xl border transition-all ${showMobileAnalysis ? 'bg-white text-black border-white' : 'bg-white/5 text-white/20 border-white/10'}`}
                    >
                        <BarChart2 size={18} />
                    </button>
                </div>
            </div>

            {/* RIGHT PANEL - Theory Drawer - Responsive */}
            <div className={`
                flex-1 flex flex-col gap-4 lg:gap-6 w-full lg:min-w-[340px] lg:max-w-[420px] 
                fixed lg:relative inset-x-0 bottom-0 z-50 lg:z-auto bg-[#0a0c10] lg:bg-transparent
                transition-transform duration-500 ease-[cubic-bezier(0.16, 1, 0.3, 1)] 
                ${showMobileAnalysis ? 'translate-y-0 h-[70vh] lg:h-full' : 'translate-y-full lg:translate-y-0 h-0 lg:h-full'} 
                lg:flex overflow-hidden shrink-0 border-t lg:border-t-0 border-white/10 shadow-[0_-20px_50px_rgba(0,0,0,0.8)] lg:shadow-none
            `}>
                {/* Mobile Drag Handle */}
                <div className="lg:hidden flex justify-center py-3 shrink-0" onClick={() => setShowMobileAnalysis(false)}>
                    <div className="w-12 h-1.5 bg-white/10 rounded-full" />
                </div>

                <div className="flex-1 overflow-y-auto p-4 lg:p-0 flex flex-col gap-4 custom-scrollbar">
                    {/* Theory Commentary or Coach Panel */}
                    <div className="shrink-0">
                        {isDeviated && evidence ? (
                            <CoachPanel
                                evidence={evidence}
                                onAskCoach={handleAskCoach}
                                coachExplanation={coachExplanation}
                                coachLoading={coachLoading}
                            />
                        ) : (
                            <div className="bg-[#1a1c24] rounded-3xl border border-white/5 p-6 shadow-2xl relative overflow-hidden group">
                                <div className="absolute top-0 left-0 w-1.5 h-full bg-indigo-500 shadow-[0_0_15px_rgba(99,102,241,0.5)]" />
                                <div className="flex items-center gap-3 mb-4">
                                    <BookOpen size={16} className="text-white/20" />
                                    <span className="text-[10px] font-black text-white/30 uppercase tracking-[0.3em]">Concepto Teórico</span>
                                </div>
                                <p className="text-sm lg:text-base font-black italic text-white/90 leading-relaxed mb-4">
                                    {currentExplanation ? `"${currentExplanation}"` : "Empieza a mover para descubrir la profundidad de esta apertura..."}
                                </p>
                                {!isDeviated && moveHistory.length < mainLine.length && (
                                    <div className="text-[10px] font-bold text-indigo-400 bg-indigo-500/10 px-3 py-1.5 rounded-xl border border-indigo-500/20 inline-flex items-center gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-indigo-400" /> Siguiente: {mainLine[moveHistory.length]}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* HISTORY GRID */}
                    <div className="bg-[#1a1c24] rounded-3xl border border-white/5 p-5 flex-1 flex flex-col gap-4 shadow-2xl min-h-0 overflow-hidden">
                        <div className="flex items-center gap-3 mb-1">
                            <Terminal size={12} className="text-white/20" />
                            <h4 className="text-[10px] font-black text-white/30 tracking-[0.3em] uppercase">Secuencia de Línea</h4>
                        </div>
                        <div className="flex-1 overflow-y-auto pr-1 flex flex-wrap gap-2 content-start custom-scrollbar">
                            {moveHistory.length === 0 ? (
                                <div className="w-full py-12 text-center text-[10px] text-white/10 font-black italic uppercase tracking-[0.3em] border border-dashed border-white/5 rounded-2xl">Línea base...</div>
                            ) : (
                                moveHistory.map((m, i) => (
                                    <button
                                        key={i}
                                        onClick={() => goToIndex(i + 1)}
                                        className={`flex items-center gap-2 py-2 px-4 rounded-xl border transition-all ${i === moveHistory.length - 1 ? 'bg-indigo-500 text-white font-black border-indigo-400 shadow-[0_0_15px_rgba(99,102,241,0.2)]' : 'bg-white/3 text-white/40 border-white/5 hover:bg-white/5'}`}
                                    >
                                        <span className={`text-[9px] font-black ${i === moveHistory.length - 1 ? 'text-black/40' : 'text-white/10'} tabular-nums`}>{Math.floor(i / 2) + 1}{i % 2 === 0 ? '.' : '..'}</span>
                                        <span className="text-[11px] font-bold uppercase tracking-tight">{m}</span>
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OpeningsTheory;
